// ============================================================
// Contrack AI — Onboarding localization (Norwegian + English)
// Language follows the app's i18next instance (browser-detected:
// localStorage `contrack_lang` → navigator). The NO|EN toggle in the
// top bar switches it live. Amounts are formatted per locale + currency
// (nb → NOK / nb-NO, en → USD / en-US).
// ============================================================
import { useTranslation } from "react-i18next";

export type Lang = "nb" | "en";

export interface ObCopy {
  // intro
  introWords: [string, string, string];
  skipHint: string;
  welcomeTitle: string;
  welcomeSub: string;
  cta: string;
  discrepancyFound: (amount: string) => string;
  // chrome / footer
  back: string;
  skip: string;
  continueLabel: string;
  finishSetup: string;
  // step country / contract-type
  stepCountryTitle: string;
  stepCountrySub: string;
  countries: Record<string, string>;
  stepContractTitle: string;
  stepContractSub: string;
  contractTypes: Record<string, { title: string; desc: string }>;
  // step 1
  step1Title: string;
  step1Sub: string;
  industries: Record<string, { title: string; desc: string }>;
  otherPlaceholder: string;
  // step 2
  step2Title: string;
  step2Sub: string;
  goals: Record<string, { title: string; desc: string }>;
  // step 3
  step3Title: string;
  step3Q1: string;
  step3Q2: string;
  // step 4
  step4Title: string;
  step4Sub: string;
  emailTitle: string;
  emailBody: string;
  erpTitle: string;
  erpBody: string;
  erpBadge: string;
  connected: string;
  step4Skip: string;
  // step 5 (building)
  buildingHeading: string;
  building: {
    validation: Record<string, string>;
    lineByLine: string;
    balance: string;
    approval: string;
    audit: string;
    projects: (count: string) => string;
    connectBoth: string;
    connectEmail: string;
    connectErp: string;
    workspace: string;
  };
}

export const OB_COPY: Record<Lang, ObCopy> = {
  nb: {
    introWords: ["Hver faktura.", "Hver linje.", "Kontrollert."],
    skipHint: "Klikk for å hoppe over",
    welcomeTitle: "Velkommen til Contrack",
    welcomeSub: "AI-en din leser hver linje mot kontrakten, før du betaler.",
    cta: "Kom i gang",
    discrepancyFound: (a) => `Avvik funnet: ${a}`,
    back: "Tilbake",
    skip: "Hopp over",
    continueLabel: "Fortsett",
    finishSetup: "Fullfør oppsett",
    stepCountryTitle: "Hvilket marked opererer dere i?",
    stepCountrySub: "Vi tilpasser kontraktsvalidering til riktig nasjonal standard.",
    countries: { no: "Norge", me: "Midtøsten", pl: "Polen", intl: "Internasjonal",
                 se: "Sverige", dk: "Danmark", fi: "Finland", de: "Tyskland", fr: "Frankrike" },
    stepContractTitle: "Hvilken kontraktsstandard bruker dere?",
    stepContractSub: "Bestemmer hvilke bestemmelser fakturaene valideres mot.",
    contractTypes: {
      NS8405: { title: "NS 8405", desc: "Norsk bygge- og anleggskontrakt" },
      NS8407: { title: "NS 8407", desc: "Norsk totalentreprise" },
      FIDIC: { title: "FIDIC", desc: "Internasjonal entreprisestandard (Yellow Book)" },
      AB04: { title: "AB 04", desc: "Svensk utførelsesentreprise" },
      ABT06: { title: "ABT 06", desc: "Svensk totalentreprise" },
      AB18: { title: "AB 18", desc: "Dansk utførelsesentreprise" },
      ABT18: { title: "ABT 18", desc: "Dansk totalentreprise" },
      YSE1998: { title: "YSE 1998", desc: "Finsk entreprisestandard" },
      VOBB: { title: "VOB/B", desc: "Tysk entreprisestandard" },
      NFP03001: { title: "NF P03-001", desc: "Fransk entreprisestandard" },
    },
    step1Title: "Hva beskriver virksomheten din?",
    step1Sub: "Vi tilpasser Contrack til din bransje: terminologi, valideringsregler og arbeidsflyt.",
    industries: {
      bygg: { title: "Bygg & anlegg", desc: "Underentrepriser, hovedkontrakter, endringsordrer" },
      eiendom: { title: "Eiendomsutvikling", desc: "Utbyggingsprosjekter, hovedentrepriser, byggelån" },
      restaurant: { title: "Restaurant & servering", desc: "Vareleveranser, rammeavtaler, prisavvik" },
      logistikk: { title: "Logistikk & transport", desc: "Fraktavtaler, drivstofftillegg, volumrabatter" },
      industri: { title: "Industri & produksjon", desc: "Råvarekontrakter, innkjøpsavtaler, leveranser" },
      annet: { title: "Annet", desc: "Fortell oss om din bransje" },
    },
    otherPlaceholder: "Beskriv bransjen din",
    step2Title: "Hva er ditt primære mål med Contrack?",
    step2Sub: "Velg inntil to. Dette bestemmer hva du ser først.",
    goals: {
      feil: { title: "Fange feil før betaling", desc: "Avdekk pris-, mengde- og spesifikasjonsavvik automatisk" },
      saldo: { title: "Kontroll på kontraktssaldo", desc: "Se gjenstående verdi per kontrakt i sanntid" },
      attestering: { title: "Raskere attestering", desc: "Strukturert godkjenningsflyt for hele teamet" },
      revisjon: { title: "Revisjonsspor og dokumentasjon", desc: "Full historikk på hver linje, hver beslutning" },
    },
    step3Title: "Litt om volumet deres",
    step3Q1: "Omtrent hvor mange leverandørfakturaer mottar dere per måned?",
    step3Q2: "Hvor mange aktive prosjekter eller kontrakter har dere?",
    step4Title: "Koble til fakturastrømmen din",
    step4Sub: "Contrack henter fakturaer automatisk. Du kan koble til begge.",
    emailTitle: "E-post",
    emailBody: "Vi henter leverandørfakturaer direkte fra innboksen.",
    erpTitle: "Regnskapssystem",
    erpBody: "Synkroniser fakturaer og attestering med regnskapssystemet.",
    erpBadge: "Flere systemer kommer",
    connected: "Tilkoblet",
    step4Skip: "Hopp over, last opp fakturaer manuelt senere",
    buildingHeading: "Setter opp Contrack for deg",
    building: {
      validation: {
        bygg: "Aktiverer kontraktsvalidering…",
        eiendom: "Aktiverer hovedentreprise- og byggelånskontroll…",
        restaurant: "Aktiverer rammeavtale-kontroll og prisavviksvarsling…",
        logistikk: "Aktiverer frakt- og drivstofftillegg-validering…",
        industri: "Aktiverer råvare- og innkjøpsavtale-validering…",
        annet: "Aktiverer kontraktsvalidering for din bransje…",
      },
      lineByLine: "Setter opp linje-for-linje fakturakontroll…",
      balance: "Beregner gjenstående kontraktssaldo i sanntid…",
      approval: "Bygger strukturert attesteringsflyt…",
      audit: "Klargjør fullstendig revisjonsspor per linje…",
      projects: (c) => `Konfigurerer prosjektstruktur for ${c} prosjekter…`,
      connectBoth: "Kobler til e-postmottak og regnskapssystem…",
      connectEmail: "Kobler til e-postmottak…",
      connectErp: "Synkroniserer med regnskapssystemet…",
      workspace: "Klargjør ditt arbeidsområde…",
    },
  },
  en: {
    introWords: ["Every invoice.", "Every line.", "Verified."],
    skipHint: "Click to skip",
    welcomeTitle: "Welcome to Contrack",
    welcomeSub: "Your AI reads every line against the contract, before you pay.",
    cta: "Get started",
    discrepancyFound: (a) => `Discrepancy found: ${a}`,
    back: "Back",
    skip: "Skip",
    continueLabel: "Continue",
    finishSetup: "Finish setup",
    stepCountryTitle: "Which market do you operate in?",
    stepCountrySub: "We tailor contract validation to the right national standard.",
    countries: { no: "Norway", me: "Middle East", pl: "Poland", intl: "International",
                 se: "Sweden", dk: "Denmark", fi: "Finland", de: "Germany", fr: "France" },
    stepContractTitle: "Which contract standard do you use?",
    stepContractSub: "Determines which provisions invoices are validated against.",
    contractTypes: {
      NS8405: { title: "NS 8405", desc: "Norwegian construction contract" },
      NS8407: { title: "NS 8407", desc: "Norwegian design-and-build" },
      FIDIC: { title: "FIDIC", desc: "International construction standard (Yellow Book)" },
      AB04: { title: "AB 04", desc: "Swedish construction contract" },
      ABT06: { title: "ABT 06", desc: "Swedish design-and-build" },
      AB18: { title: "AB 18", desc: "Danish construction contract" },
      ABT18: { title: "ABT 18", desc: "Danish design-and-build" },
      YSE1998: { title: "YSE 1998", desc: "Finnish construction standard" },
      VOBB: { title: "VOB/B", desc: "German construction standard" },
      NFP03001: { title: "NF P03-001", desc: "French construction standard" },
    },
    step1Title: "What describes your business?",
    step1Sub: "We tailor Contrack to your industry: terminology, validation rules and workflow.",
    industries: {
      bygg: { title: "Construction", desc: "Subcontracts, main contracts, change orders" },
      eiendom: { title: "Real estate development", desc: "Development projects, main contracts, construction loans" },
      restaurant: { title: "Restaurant & hospitality", desc: "Goods deliveries, framework agreements, price deviations" },
      logistikk: { title: "Logistics & transport", desc: "Freight agreements, fuel surcharges, volume discounts" },
      industri: { title: "Industry & manufacturing", desc: "Raw-material contracts, purchasing agreements, deliveries" },
      annet: { title: "Other", desc: "Tell us about your industry" },
    },
    otherPlaceholder: "Describe your industry",
    step2Title: "What's your primary goal with Contrack?",
    step2Sub: "Choose up to two. This determines what you see first.",
    goals: {
      feil: { title: "Catch errors before payment", desc: "Automatically detect price, quantity and specification deviations" },
      saldo: { title: "Control over contract balance", desc: "See remaining value per contract in real time" },
      attestering: { title: "Faster approval", desc: "Structured approval flow for the whole team" },
      revisjon: { title: "Audit trail and documentation", desc: "Full history on every line, every decision" },
    },
    step3Title: "A bit about your volume",
    step3Q1: "Roughly how many supplier invoices do you receive per month?",
    step3Q2: "How many active projects or contracts do you have?",
    step4Title: "Connect your invoice stream",
    step4Sub: "Contrack fetches invoices automatically. You can connect both.",
    emailTitle: "Email",
    emailBody: "We fetch supplier invoices directly from your inbox.",
    erpTitle: "Accounting system",
    erpBody: "Sync invoices and approvals with your accounting system.",
    erpBadge: "More systems coming",
    connected: "Connected",
    step4Skip: "Skip, upload invoices manually later",
    buildingHeading: "Setting up Contrack for you",
    building: {
      validation: {
        bygg: "Activating contract validation…",
        eiendom: "Activating main-contract and construction-loan checks…",
        restaurant: "Activating framework-agreement checks and price-deviation alerts…",
        logistikk: "Activating freight and fuel-surcharge validation…",
        industri: "Activating raw-material and purchasing-agreement validation…",
        annet: "Activating contract validation for your industry…",
      },
      lineByLine: "Setting up line-by-line invoice control…",
      balance: "Calculating remaining contract balance in real time…",
      approval: "Building structured approval flow…",
      audit: "Preparing full audit trail per line…",
      projects: (c) => `Configuring project structure for ${c} projects…`,
      connectBoth: "Connecting to email intake and accounting system…",
      connectEmail: "Connecting to email intake…",
      connectErp: "Syncing with the accounting system…",
      workspace: "Preparing your workspace…",
    },
  },
};

// Currency code shown in the intro's invoice scan (per the user's USD example).
export function obCurrencyCode(lang: Lang): string {
  return lang === "en" ? "USD" : "NOK";
}

// Format an amount as "CODE 84 200" (nb-NO) / "CODE 84,200" (en-US).
export function fmtAmount(value: number, lang: Lang): string {
  const locale = lang === "en" ? "en-US" : "nb-NO";
  const n = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value);
  return `${obCurrencyCode(lang)} ${n}`;
}

// Resolve the active onboarding language from the app's i18n instance, and
// expose a setter that switches the whole app (persists to contrack_lang).
export function useOnboardingLang() {
  const { i18n } = useTranslation();
  const resolved = (i18n.resolvedLanguage || i18n.language || "nb").toLowerCase();
  const lang: Lang = resolved.startsWith("en") ? "en" : "nb";
  const setLang = (l: Lang) => { if (l !== lang) i18n.changeLanguage(l); };
  return { lang, setLang, copy: OB_COPY[lang] };
}
